import React, { useEffect, useState } from 'react';
import { addLogComment, getLogsByID, addLog } from '../services/api/implementation/impl';
import { useQuery } from '@tanstack/react-query';
import LogCard from '../components/layout/LogCard';
import NoDataFoundImg from '../../shared/assets/png/noDataFound.png';
import toast from 'react-hot-toast';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { Plus, X } from 'lucide-react';
import Button from '../../shared/components/ui/Button';
import Modal from '../../shared/components/ui/Modal';
import axios from 'axios';

const Logs = ({ salesId }) => {
  const userId = Cookies.get('userId');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddLogModal, setShowAddLogModal] = useState(false);
  const [logDescription, setLogDescription] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const {
    data: activityId,
    refetch: refetchLeadbyId,
    isFetching
  } = useQuery({
    queryKey: ['getLogsByID', salesId],
    queryFn: () => getLogsByID(salesId),
    enabled: !!salesId
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('Logs Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    refetchLeadbyId();
  }, [salesId]);

  const handleCommentSubmit = async (logId, commentText) => {
    if (!commentText.trim()) return;

    setIsSubmitting(true);
    const payload = {
      comment: commentText,
      log_id: Number(logId)
    };

    try {
      const res = await addLogComment(payload);

      await refetchLeadbyId({ cancelRefetch: false });
    } catch (error) {
      console.error('Error submitting comment:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to add comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const token = Cookies.get('token');
      if (!token) throw new Error('No token found');

      const formData = new FormData();
      formData.append('files', file);

      const res = await axios.post('https://application.housingram.com/api/s3/upload', formData, {
        headers: {
          authorization: token,
          'Content-Type': 'multipart/form-data'
        }
      });

      const uploaded = res.data.data.files[0];
      setUploadedFile({
        name: uploaded.originalName,
        url: uploaded.presignedUrl
      });
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleAddLog = async () => {
    if (!logDescription) {
      toast.error('Please enter log description');
      return;
    }

    const payload = {
      name: logDescription,
      description: logDescription,
      sales_user_id: Number(userId),
      lead_id: salesId,
      file: uploadedFile?.url || ''
    };

    console.log('PAYLOAD', JSON.stringify(payload));

    try {
      setIsSubmitting(true);
      await addLog(payload);
      toast.success('Log added successfully');
      setShowAddLogModal(false);
      setLogDescription('');
      setUploadedFile(null);
      await refetchLeadbyId();
    } catch (err) {
      console.error('Error in add log:', err);
      toast.error(err?.response?.data?.message || 'Error in add log');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col bg-white shadow-lg rounded-lg mt-[0.5rem] w-[100%] mx-auto p-6">
      <div className="fixed bottom-8 right-8 z-50">
        <Button
          displayType="primary"
          onClick={() => {
            setShowAddLogModal(true);
          }}
          customStyle={{
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}>
          <Plus size={24} color="white" />
        </Button>
      </div>

      <span className="text-lg font-semibold">Logs</span>

      {Array.isArray(activityId) && activityId.length > 0 && (
        <div className="w-full flex flex-col justify-between items-center gap-8 mt-10">
          {activityId.map((item, index) => (
            <LogCard
              key={index}
              special={index === 0}
              data={item}
              isSubmitting={isSubmitting}
              onCommentSubmit={handleCommentSubmit}
            />
          ))}
        </div>
      )}

      {(!Array.isArray(activityId) || activityId.length === 0) && (
        <div className={`flex items-center justify-center bg-gray-100 p-4 `}>
          <div className="text-center border flex flex-col items-center justify-center gap-2 border-gray-300 rounded-lg p-6 w-[400px] h-[400px] shadow-lg">
            <img
              src={NoDataFoundImg}
              alt="No Data"
              className="w-[50%] mx-auto mb-4 object-contain"
            />
            <h2 className="text-xl font-bold text-gray-800">Dear User</h2>
            <p className="text-gray-600 mb-6">The page you are trying to access has no data.</p>
          </div>
        </div>
      )}

      {/* Add Log Modal */}
      {showAddLogModal && (
        <Modal
          isDirty
          confirmOnClose
          width={50}
          name="Add Log"
          onClose={() => {
            setShowAddLogModal(false);
            setLogDescription('');
            setUploadedFile(null);
          }}>
          <div className="flex flex-col space-y-4">
            {/* Log Description Field */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Log Description <span className="text-red-500">*</span>
              </label>
              <textarea
                placeholder="Enter log description here..."
                value={logDescription}
                onChange={(e) => setLogDescription(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* File Upload */}
            {/* <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attachment (Optional)
              </label>
              <div className="flex items-center space-x-2">
                <label
                  htmlFor="log-file-upload"
                  className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                  <input
                    id="log-file-upload"
                    type="file"
                    className="sr-only"
                    onChange={handleFileUpload}
                    accept="image/*,.pdf,.doc,.docx"
                    disabled={uploading}
                  />
                  {uploading ? 'Uploading...' : 'Choose File'}
                </label>
                {uploadedFile && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <span className="truncate max-w-xs">{uploadedFile.name}</span>
                    <button
                      onClick={() => setUploadedFile(null)}
                      className="text-red-500 hover:text-red-700">
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div> */}

            {/* Action Buttons */}
            <div className="w-full flex justify-end items-center space-x-4 mt-6 pt-4 border-t">
              <Button
                type="reset"
                onClick={() => {
                  setShowAddLogModal(false);
                  setLogDescription('');
                  setUploadedFile(null);
                }}
                displayType="secondary">
                Cancel
              </Button>
              <Button disabled={isSubmitting} type="button" onClick={handleAddLog}>
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Logs;
